import { useMutation, useQuery } from '@tanstack/vue-query';
import {
  CourierReportPayload,
  CustomerInvoicesPayload,
  HblLotKpiPayload,
  ImportsReportsHoldGeneratePayload,
  ImportsReportsMonthlyGeneratePayload,
  MblLotKpiPayload,
  ReportInvoiceFilters,
  WindowReleasePayload,
  FlatInvoicePayload,
  InternalInventoryPayload,
  SalesJournalPayload,
} from '@/types/imports/reports/imports.reports.types';
import ImportReportsService, { PeriodReportPayload } from '@/services/reports/reports.service';
import { MaybeRef } from '@/types/utils';
import { unref } from 'vue';
import { useToast } from '@/composables/useToast';
import { AxiosError } from 'axios';

export const useReports = () => {
  const toast = useToast();
  const useGenerateMonthlyReport = () => {
    return useMutation(async (payload: ImportsReportsMonthlyGeneratePayload) => {
      const { data: report } = await ImportReportsService.generateMonthlyOrigin(payload);
      return report;
    });
  };

  const useGenerateHoldReport = () => {
    return useMutation(async (payload: ImportsReportsHoldGeneratePayload) => {
      const { data: holdReport } = await ImportReportsService.generateHold(payload);
      return holdReport;
    });
  };

  const useGetEmptyUnitReport = () => {
    return useQuery(['empty-unit-report'], async () => {
      const { data } = await ImportReportsService.getEmptyUnitReport();
      return data;
    });
  };

  const useDownloadEmptyUnitReport = () => {
    return useQuery(
      ['empty-unit-report-xslx'],
      async () => {
        const { data } = await ImportReportsService.downloadEmptyUnitReport();
        return data;
      },
      { enabled: false }
    );
  };
  // const useDownloadUnitInventoryReport = () => {
  //   return useQuery(
  //     ['unit-inventory-report-xslx'],
  //     async () => {
  //       const {
  //         data: { base64Data },
  //       } = await ImportReportsService.downloadUnitInventoryReport();
  //       return base64Data;
  //     },
  //     { enabled: false }
  //   );
  // };
  const useDownloadInventoryReport = (customer: MaybeRef<string>) => {
    return useQuery(
      ['inventory-report-xslx', customer],
      async () => {
        const customerValue = unref(customer);
        if (!customerValue.length) {
          toast.info('Selected customer is missing!');
          return;
        }
        const { data } = await ImportReportsService.downloadInventoryReport(customerValue);
        return data;
      },
      { enabled: false }
    );
  };
  const useDownloadWarehouseReport = () => {
    return useMutation(async (payload: PeriodReportPayload) => {
      if (!payload.customer.length) {
        toast.info('Selected customer is missing!');
        return;
      }
      const { data } = await ImportReportsService.downloadWarehouseReport(payload);
      return data;
    });
  };
  const useDownloadCommissionReport = () => {
    return useMutation(
      async (payload: PeriodReportPayload) => {
        if (!payload.customer?.length) {
          toast.info('Selected customer is missing!');
          return;
        }
        const { data } = await ImportReportsService.downloadCommissionReport(payload);
        return data;
      },
      {
        retry: false,
        onError: (error: AxiosError<string>) => toast.error(error?.response?.data ?? 'Error!'),
      }
    );
  };
  const useDownloadCommissionReportBatch = () => {
    return useMutation(
      async (customer: MaybeRef<string[]>) => {
        const customerValue = unref(customer);
        if (!customerValue?.length) {
          toast.info('Selected customer is missing!');
          return;
        }
        const { data } = await ImportReportsService.downloadCommissionReportBatch(customerValue);
        return data;
      },
      {
        retry: false,
        onError: () => toast.error('Error while downloading report!'),
      }
    );
  };
  const useFinalizeCommissionReport = () => {
    return useMutation(
      async (payload: PeriodReportPayload & { isPending: boolean }) => {
        if (!payload.customer?.length) {
          toast.info('Selected customer is missing!');
          return;
        }
        const {
          data: { message },
        } = await ImportReportsService.finalizeCommissionReport(payload);
        return message;
      },
      {
        onSuccess: (message) => {
          toast.info(message as string);
        },
        onError: (error: AxiosError<string>) => toast.error(error?.response?.data ?? 'Error!'),
      }
    );
  };
  const useFinalizeWarehouse = () => {
    return useMutation(
      async (payload: PeriodReportPayload & { isPending: boolean }) => {
        if (!payload.customer?.length) {
          toast.info('Selected customer is missing!');
          return;
        }

        const {
          data: { message },
        } = await ImportReportsService.finalizeWarehouseReport(payload);
        return message;
      },
      {
        onSuccess: (message) => {
          toast.info(message as string);
        },
        onError: (error: AxiosError<string>) => toast.error(error?.response?.data ?? 'Error!'),
      }
    );
  };

  const useDownloadInvoiceReport = (filters: MaybeRef<ReportInvoiceFilters>) => {
    return useQuery(
      ['invoice-report-xslx', filters],
      async () => {
        const { data } = await ImportReportsService.downloadInvoiceReport(unref(filters));
        return data;
      },
      {
        enabled: false,
        onError: (error: AxiosError<string>) => toast.error(error?.response?.data ?? 'Error!'),
      }
    );
  };

  const useDownloadInternalInventoryReport = () => {
    return useMutation(
      async (filters: InternalInventoryPayload & Record<string, string>) => {
        const { outputType, documentType, ...queryParams } = filters;
        const buffer = await ImportReportsService.downloadInternalInventoryReport(
          { outputType, documentType },
          queryParams ?? {}
        );
        return buffer;
      },
      {
        retry: false,
        onError: () => toast.error('No Data Available'),
      }
    );
  };

  const useDownloadIpiAgingReport = () => {
    return useMutation(
      async () => {
        const buffer = await ImportReportsService.downloadIpiAgingReport();
        return buffer;
      },
      {
        retry: false,
        onError: () => toast.error('No Data Available'),
      }
    );
  };

  const useDownloadCustomerInvoicesReport = () => {
    return useMutation(
      async (payload: CustomerInvoicesPayload) => {
        if (!payload.customer?.length) {
          toast.info('Selected customer is missing!');
          return;
        }

        const { data } = await ImportReportsService.downloadCustomerInvoicesReport(payload);
        return data;
      },
      {
        retry: false,
      }
    );
  };

  const useDownloadHblLotKpiReport = () => {
    return useMutation(async (payload: HblLotKpiPayload) => {
      const { data } = await ImportReportsService.downloadHblLotKpiReport(payload);
      return data;
    });
  };

  const useDownloadMblLotKpiReport = () => {
    return useMutation(async (payload: MblLotKpiPayload) => {
      const { data } = await ImportReportsService.downloadMblLotKpiReport(payload);
      return data;
    });
  };

  const useDownloadReleasedToGoReport = () => {
    return useMutation(async () => {
      const { data } = await ImportReportsService.downloadReleasedToGoReport();
      return data;
    });
  };

  const useDownloadCourierReport = () => {
    return useMutation(async (payload: CourierReportPayload) => {
      const { data } = await ImportReportsService.downloadCourierReport(payload);
      return data;
    });
  };

  const useFinalizeCourierReport = () => {
    return useMutation(async (payload: CourierReportPayload) => {
      const { data } = await ImportReportsService.finalizeCourierReport(payload);
      return data;
    });
  };

  const useDownloadWindowReleaseReport = () => {
    return useMutation(async (payload: WindowReleasePayload) => {
      const { data } = await ImportReportsService.downloadWindowReleaseReport(payload);
      return data;
    });
  };

  const useDownloadFlatInvoiceReport = () => {
    return useMutation(async (payload: FlatInvoicePayload) => {
      const { data } = await ImportReportsService.downloadFlatInvoiceReport(payload);
      return data;
    });
  };

  const useDownloadSalesJournalReport = () => {
    return useMutation(
      async (payload: SalesJournalPayload) => {
        const { data } = await ImportReportsService.downloadSalesJournalReport(payload);
        return data;
      },
      {
        retry: false,
        onError: (error: AxiosError<string>) =>
          toast.error(error?.response?.data ?? 'Error downloading report'),
      }
    );
  };

  return {
    useDownloadIpiAgingReport,
    useFinalizeWarehouse,
    useDownloadWarehouseReport,
    useGenerateHoldReport,
    useGenerateMonthlyReport,
    useGetEmptyUnitReport,
    useDownloadEmptyUnitReport,
    useDownloadCommissionReport,
    useDownloadCommissionReportBatch,
    useFinalizeCommissionReport,
    useDownloadInvoiceReport,
    useDownloadInventoryReport,
    useDownloadInternalInventoryReport,
    useDownloadCustomerInvoicesReport,
    useDownloadHblLotKpiReport,
    useDownloadMblLotKpiReport,
    useDownloadReleasedToGoReport,
    useDownloadCourierReport,
    useFinalizeCourierReport,
    useDownloadWindowReleaseReport,
    useDownloadFlatInvoiceReport,
    useDownloadSalesJournalReport,
  };
};
